import { options } from '!/flags'
import { register } from '!/repository'
import { model as router } from '!/router'
import { model as bannersModel } from '$/banners'
import { model as moviesAllCategoryModel } from '$/movies/all-category'
import { model as moviesFeaturedModel } from '$/movies/featured'
import { genPagesLogic } from '@/effector'
import type { VodUpdateObj } from '@setplex/tria-api'
import { api } from '@setplex/tria-api'
import { createEvent, restore, sample } from 'effector'
import { and, not, or } from 'patronum'
import { model as movieBundleModel } from '~/entities/movies/bundles'
import { model as movieCategoriesModel } from '~/entities/movies/categories'
import { model as paymentModel } from '~/features/payment/generic'
import '~/shared/ui/translations/general-translations'
import { $moviesTitles } from './translations'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9
// *
// * events
// *
export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()

export const load = createEvent<{
  page: number
  count?: number
}>()

export const updateVod = createEvent<VodUpdateObj>()

export const seeAllMovieClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieClick,
  fn: ({ id }) => `/content/movie/categories/${id}`,
  target: router.navigatePush,
})

export const seeAllMovieCategoryClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieCategoryClick,
  fn: ({ id }) => `/content/movie/categories/${id}/subcategories`,
  target: router.navigatePush,
})

interface IRedirectToMoviePlayerParams {
  id?: number
  categoryId?: number
}

export const redirectToMoviePlayer = createEvent<IRedirectToMoviePlayerParams>()

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

sample({
  clock: redirectToMoviePlayer,
  fn: ({ id, categoryId }) =>
    `/player/movie?vodId=${id}&categoryId=${categoryId}`,
  target: router.navigatePush,
})

// *
// * CONNECTIONS
// *

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

sample({
  clock: updateVod,
  fn: (params) => ({ ...params, favorite: !params.favorite }),
  target: api.vods.updateVodFx,
})

const loadMoreCategoriesWithVods = genPagesLogic(
  api.vods.$pageInfoCategoriesWithVods,
  api.vods.loadCategoriesWithVodsFx
)

sample({
  clock: load,
  filter: not(api.vods.initCategoriesWithVodsFx.pending),
  target: api.vods.initCategoriesWithVodsFx,
})
sample({
  clock: load,
  filter: not(api.banners.getBannersFx.pending),
  target: api.banners.getBannersFx,
})
sample({
  clock: load,
  filter: not(api.banners.getFeaturedCarouselFx.pending),
  target: api.banners.getFeaturedCarouselFx,
})
sample({
  clock: load,
  filter: not(api.vods.getLastAddedMoviesFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.getLastAddedMoviesFx,
})
sample({
  clock: load,
  filter: not(api.vodBundles.initVodBundlesRowByPageFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vodBundles.initVodBundlesRowByPageFx,
})
sample({
  clock: load,
  filter: not(api.vods.initInfiniteVodsCategoryFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.initInfiniteVodsCategoryFx,
})

// *
// * stores
// *
/********** Skeleton logics starts ***********/
const $isAnyInitFxPending = or(
  // TODO attach fx`s needed
  api.banners.getBannersFx.pending,
  api.banners.getFeaturedCarouselFx.pending,
  api.vods.initCategoriesWithVodsFx.pending,
  api.vodBundles.initVodBundlesRowByPageFx.pending,
  api.vods.initInfiniteVodsCategoryFx.pending
)

sample({
  clock: $isAnyInitFxPending,
  source: { pathname: router.$pathname },
  filter: ({ pathname }) => pathname === '/modules/movie',
  fn: (_, clockData) => clockData,
  target: setContentLoading,
})
/********** Skeleton logics ends ***********/

const $bannerVodCarouselShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersTypeVodFormatted.map((banners) =>
    banners.some(({ bannerBundles }) => bannerBundles?.length)
  )
)

const $featuredVodCarouselShown = and(
  options.$featuredCarouselsEnabled,
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted.map((carousel) =>
    carousel?.some(({ enabledOnModulePage }) => enabledOnModulePage)
  )
)

// clear paginatable stores
api.banners.$bannersTypeVod.reset(init)
api.vodBundles.$vodBundlesRow.reset(init)
api.vods.$categoriesWithVods.reset(init) // NOT necessary anymore, coz init event refills store (removing would show prev data on page init)

register(seeAllMovieClick, 'seeAllMovieClick')
register(seeAllMovieCategoryClick, 'seeAllMovieCategoryClick')
register(updateVod, 'updateVod')
register(bannersModel.$bannersTypeVodFormatted, '$bannersTypeVod')

register($bannerVodCarouselShown, '$bannerVodCarouselShown')
register(
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted,
  '$featuredCarouselTypeVod'
)
register(
  moviesFeaturedModel.$featuredCarouselVodTitle,
  '$featuredCarouselVodTitle'
)
register(movieBundleModel.$vodBundlesRowFormatted, '$vodBundlesRow')
register(movieBundleModel.$vodBundlesRowTitle, '$vodBundlesRowTitle')
register(
  movieCategoriesModel.$categoriesWithVodsFormattedVods,
  '$categoriesWithVodsFormattedVods'
) // api.vods.$categoriesWithVods
register($featuredVodCarouselShown, '$featuredVodCarouselShown')
register(loadMoreCategoriesWithVods, 'loadMoreCategoriesWithVods')
register(paymentModel.selectMoviePaymentButton, 'selectMoviePaymentButton')
register(api.vods.$pageInfoCategoriesWithVods, '$pageInfoCategoriesWithVods')
register(
  moviesAllCategoryModel.$infiniteVodsFormatted,
  '$infiniteVodsFormatted'
)
register(moviesAllCategoryModel.$infiniteVodsTitle, '$infiniteVodsTitle')

register($moviesTitles, '$moviesTitles')
